import React, { type ReactNode } from 'react';
import { MdLogout } from 'react-icons/md';
import {
  PROFILE_MENU_ITEM_VARIANT_DEFAULT,
  PROFILE_MENU_ITEM_VARIANT_DANGER,
  type ProfileMenuItemVariant,
} from './ProfileMenu.constants';
import {
  Body,
  Divider,
  Footer,
  Header,
  HeaderMeta,
  HeaderName,
  MenuItem,
  MenuItemIcon,
  MenuItemLabel,
  Panel,
  SectionLabel,
  SectionWrapper,
} from './ProfileMenu.styled';

export interface ProfileMenuProps {
  /** Display name shown in the header (e.g. "Sam Kowalski"). Omit to hide the header. */
  name?: string;
  /** Secondary line in the header (typically the user's email). */
  meta?: string;
  children?: ReactNode;
  dataTestId?: string;
}

interface SectionProps {
  label?: string;
  children?: ReactNode;
  dataTestId?: string;
}

interface ItemProps {
  icon?: ReactNode;
  children: ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  variant?: ProfileMenuItemVariant;
  dataTestId?: string;
}

interface FooterSlotProps {
  children?: ReactNode;
}

interface LogOutItemProps {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  dataTestId?: string;
  /** Allow apps to localise the label without losing the variant + icon. */
  label?: string;
}

/**
 * Anchored dropdown panel intended to sit beneath an Avatar trigger.
 *
 * Composition contract:
 *   <ProfileMenu name="..." meta="...">          // header (auto-rendered)
 *     <ProfileMenu.Section label="Notifications">
 *       ...arbitrary children (e.g. a notification list)
 *     </ProfileMenu.Section>
 *     <ProfileMenu.Item icon={...} variant="danger" onClick={...}>
 *       Log Out
 *     </ProfileMenu.Item>
 *   </ProfileMenu>
 *
 * Open/close lifecycle and outside-click handling are owned by the parent —
 * this component is intentionally dumb so it can be reused in any layout.
 */
const ProfileMenuRoot: React.FC<ProfileMenuProps> = ({
  name,
  meta,
  children,
  dataTestId = 'profile-menu',
}) => {
  const childArray = React.Children.toArray(children).filter(Boolean);

  return (
    <Panel
      role="menu"
      aria-label={name ? `Account menu — ${name}` : 'Account menu'}
      data-testid={dataTestId}
    >
      {(name || meta) && (
        <>
          <Header data-testid={`${dataTestId}__header`}>
            {name && <HeaderName>{name}</HeaderName>}
            {meta && <HeaderMeta>{meta}</HeaderMeta>}
          </Header>
          <Divider />
        </>
      )}
      {childArray.map((child, index) => {
        const reactChild = child as React.ReactElement<{ key?: React.Key }>;
        const key = reactChild.key ?? `profile-menu-child-${index}`;
        return (
          <React.Fragment key={key}>
            {index > 0 && <Divider />}
            {child}
          </React.Fragment>
        );
      })}
    </Panel>
  );
};

const Section: React.FC<SectionProps> = ({
  label,
  children,
  dataTestId = 'profile-menu__section',
}) => (
  <SectionWrapper data-testid={dataTestId}>
    {label && (
      <SectionLabel data-testid={`${dataTestId}__label`}>{label}</SectionLabel>
    )}
    <Body>{children}</Body>
  </SectionWrapper>
);

const Item: React.FC<ItemProps> = ({
  icon,
  children,
  onClick,
  variant = PROFILE_MENU_ITEM_VARIANT_DEFAULT,
  dataTestId = 'profile-menu__item',
}) => (
  <MenuItem
    type="button"
    role="menuitem"
    onClick={onClick}
    $variant={variant}
    data-testid={dataTestId}
  >
    {icon && (
      <MenuItemIcon $variant={variant} aria-hidden="true">
        {icon}
      </MenuItemIcon>
    )}
    <MenuItemLabel>{children}</MenuItemLabel>
  </MenuItem>
);

const FooterSlot: React.FC<FooterSlotProps> = ({ children }) => (
  <Footer>{children}</Footer>
);

const LogOutItem: React.FC<LogOutItemProps> = ({
  onClick,
  dataTestId = 'profile-menu__logout',
  label = 'Log Out',
}) => (
  <Item
    icon={<MdLogout size={18} aria-hidden="true" />}
    variant={PROFILE_MENU_ITEM_VARIANT_DANGER}
    onClick={onClick}
    dataTestId={dataTestId}
  >
    {label}
  </Item>
);

/**
 * Compound component shape: `ProfileMenu` is the root, and the slot
 * subcomponents (`Section`, `Item`, `LogOutItem`, `Footer`) hang off it as
 * static properties so consumers can write the JSX in one import.
 */
type ProfileMenuComponent = React.FC<ProfileMenuProps> & {
  Section: typeof Section;
  Item: typeof Item;
  LogOutItem: typeof LogOutItem;
  Footer: typeof FooterSlot;
};

const ProfileMenu = ProfileMenuRoot as ProfileMenuComponent;
ProfileMenu.Section = Section;
ProfileMenu.Item = Item;
ProfileMenu.LogOutItem = LogOutItem;
ProfileMenu.Footer = FooterSlot;

export default ProfileMenu;
